import * as rq from '@tanstack/react-query';

import { useDependencies } from '@/api/fe-api/contexts/dependencies.context';
import type { IAIRepository } from '@/api/fe-api/domain/repositories/ai-respository';

export namespace GetDocumentSummary {
  export const key = (input: Input): string[] => [
    'ai',
    'get-document-summary',
    ...Object.entries(input).flatMap(([key, value]) => [key, value]),
  ];

  export type Input = IAIRepository.GetDocumentSummaryInput;
  export type Output = IAIRepository.GetDocumentSummaryOutput;

  export function useCachedContent(input: Input) {
    const queryClient = rq.useQueryClient();
    const queryKey = key(input);
    const cachedData = queryClient.getQueryData(queryKey) as
      | { cachedContent?: string }
      | undefined;
    return cachedData?.cachedContent;
  }

  export function useQuery(input: Input) {
    const { repositories } = useDependencies();
    const queryClient = rq.useQueryClient();

    return rq.useQuery({
      queryKey: key(input),
      queryFn: async ({ queryKey }) => {
        const { stream } = await repositories.ai.getDocumentSummary(input);

        // Create two identical streams
        const [stream1, stream2] = stream.tee();

        // Start reading and caching one stream
        const contentPromise = readAndCacheStream(
          stream1,
          queryClient,
          queryKey as string[],
        );

        return {
          stream: stream2,
          contentPromise,
        };
      },
    });
  }
}

async function readAndCacheStream(
  stream: ReadableStream<Uint8Array>,
  queryClient: rq.QueryClient,
  queryKey: string[],
): Promise<string> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let result = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    result += decoder.decode(value, { stream: true });
  }

  // Cache the full content
  queryClient.setQueryData(queryKey, { cachedContent: result });

  return result;
}
